import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import useVoyagerStore from '../stores/useVoyagerStore';
import type { Progress } from '../components/Agents/api';
import { StatusChip } from '../design/primitives';
import { metaSx } from '../components/Studio/studioPresentation';
import { objectiveStatus, targetValue, trackAt } from './StromaHome';
import { ROW_KIND_LABEL, activeAt, objectiveOf, rowKind } from './recents';
import { isOpenThread } from '../stores/slices/threadsSlice';
import ChatComposer from '../components/LandingAgent/ChatComposer';

const fmt = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—'
  : Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : Number(v.toFixed(Math.abs(v) < 10 ? 2 : 1)).toLocaleString());

/**
 * An objective's space (#765), opened from the sidebar or a Your objectives
 * card: its progress, the way into its page (Plan, Activity) and the chats
 * already tied to it, and a composer whose questions are asked inside it (#766).
 */
/** The space's bar: the Objective pill, the name. */
function SpaceBar({ title, holder }: { title: string; holder?: string | null }) {
  return <Box data-testid="space-bar" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: '1px solid var(--line-soft)', bgcolor: 'background.paper' }}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, border: '1px solid var(--accent)', bgcolor: 'var(--accent-soft)', color: 'var(--accent-ink)', borderRadius: '99px', px: 1.25, py: '2px', fontSize: 12.5, fontWeight: 600 }}>
      <TrackChangesOutlinedIcon sx={{ fontSize: 13 }} />Objective
    </Box>
    <Typography component="h1" sx={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 650, m: 0 }}>{title}</Typography>
    {holder && <Typography component="span" sx={metaSx}>{holder}</Typography>}
  </Box>;
}

export default function ObjectiveSpace({ id }: { id: string }) {
  const agent = useVoyagerStore((s) => s.agents.find((a) => a.id === id) || null);
  const loading = useVoyagerStore((s) => s.agentsLoading);
  const threads = useVoyagerStore((s) => s.savedThreads);
  const s = useVoyagerStore.getState;
  const chats = React.useMemo(() => threads.filter((t) => isOpenThread(t) && objectiveOf(t, new Set([id])) === id).sort((a, b) => activeAt(b) - activeAt(a)), [threads, id]);
  // the space's composer starts on the objective's perspective (#766, #749 "from objective"), once it is known
  const presetFrom = agent ? agent.objective.perspective_id ?? null : undefined;
  React.useEffect(() => {
    if (presetFrom !== undefined) useVoyagerStore.getState().presetObjectivePick(presetFrom);
  }, [id, presetFrom]);

  if (!agent) {
    // every state of the space keeps its own way back to the sidebar (and the phone drawer)
    return <Box data-testid="objective-space" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'var(--ground)' }}>
      <SpaceBar title={loading ? 'Objective' : 'Objective unavailable'} />
      <Box sx={{ p: 3, color: 'var(--ink-3)' }}>
        {loading ? <Typography role="status">Loading the objective…</Typography> : <>
          <Typography>This objective isn't available to you.</Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => useVoyagerStore.setState({ objectiveSpaceId: null } as any)}>Back to a new question</Button>
        </>}
      </Box>
    </Box>;
  }
  const p = (agent.last_run?.target || null) as Progress | null;
  const status = objectiveStatus(agent);
  const name = agent.objective.label || agent.objective.metric.split('.').pop() || agent.objective.metric;
  const attainment = p?.attainment;
  return <Box data-testid="objective-space" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'var(--ground)' }}>
    <SpaceBar title={name} holder={agent.seat?.holder_name} />
    <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: 2.5 }}>
      <Box sx={{ maxWidth: 860, mx: 'auto', display: 'grid', gap: 2 }}>
        <Box component="section" aria-label="Progress" sx={{ bgcolor: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '14px', boxShadow: 'var(--shadow)', px: 2.25, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography component="h2" sx={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 650, m: 0 }}>{name}</Typography>
            <StatusChip status={status.tone} label={status.label} size="small" />
          </Box>
          <Typography sx={{ ...metaSx, mt: 0.5, mb: 1.5 }}>
            {[agent.seat?.holder_name, agent.objective.deadline ? `by ${agent.objective.deadline}` : null].filter(Boolean).join(' · ')}{agent.seat?.holder_name || agent.objective.deadline ? ' · ' : ''}measured by <Box component="span" sx={{ fontFamily: 'var(--mono)' }}>{agent.objective.metric}</Box>
          </Typography>
          <Box role="progressbar" aria-label={`${name} progress`} aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={attainment == null ? undefined : Math.round(Math.max(0, Math.min(1, attainment)) * 100)}
            sx={{ height: 8, bgcolor: 'var(--sunk)', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
            {attainment != null && <Box sx={{ position: 'absolute', inset: '0 auto 0 0', width: `${Math.max(trackAt(attainment) * 100, 1)}%`, background: 'linear-gradient(90deg, var(--accent), var(--accent2))', borderRadius: '4px' }} />}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, ...metaSx }}>
            <span>{fmt(p?.current)} now</span><span>target {targetValue(p, agent.objective.target)}</span>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.75 }}>
            <Button size="small" variant="outlined" onClick={() => s().navigateToObjective(agent.id)}>Open the objective page</Button>
            <Button size="small" variant="outlined" onClick={() => s().navigateToObjective(agent.id, 'plan')}>Plan</Button>
            <Button size="small" variant="outlined" onClick={() => s().navigateToObjective(agent.id, 'activity')}>Activity</Button>
          </Box>
        </Box>
        <Box component="section" aria-label={`Ask about ${name}`} data-testid="objective-ask">
          <ChatComposer variant="hero" draftKey={`objective:${agent.id}`} onSubmit={(text) => s().askInObjective(agent.id, text)} />
          <Typography sx={{ ...metaSx, mt: 0.75 }}>Questions here are asked inside this objective: Stroma reads its metric, target, guardrails and linked knowledge, and the perspective starts from the objective's.</Typography>
        </Box>
        <Box component="section" aria-label="Chats">
          <Typography sx={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)', fontWeight: 600, mb: 0.75 }}>Chats</Typography>
          <Box sx={{ bgcolor: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '14px', px: 2, py: 0.5 }}>
            {chats.map((t, i) => <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.25, borderTop: i ? '1px solid var(--line-soft)' : 0 }}>
              <Typography component="span" sx={{ ...metaSx, minWidth: 74 }}>{ROW_KIND_LABEL[rowKind(t)]}</Typography>
              <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{t.title}</Typography>
              <Button size="small" variant="outlined" onClick={() => { useVoyagerStore.setState({ objectiveSpaceId: null } as any); s().loadThread(t.id); }}>Open</Button>
            </Box>)}
            {!chats.length && <Typography sx={{ ...metaSx, py: 1.5 }}>No chats yet. Conversations with this objective show here.</Typography>}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>;
}
